

import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from '@supabase/supabase-js';
import logo from "@/assets/logo.svg";
import yieldSocialIcon from "@/assets/yieldsocial-icon.png";
import { AuthModal } from "@/components/AuthModal";
import { isFeatureEnabled } from "@/config/featureFlags";
import { MobileLeftConsole } from "@/components/MobileLeftConsole";

interface AppHeaderProps {
  isLeftRailCollapsed?: boolean;
}

export const AppHeader = ({ isLeftRailCollapsed = false }: AppHeaderProps) => {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);

  // Dial this to taste (28–36 looks very "sitting on a ledge")
  const OVERHANG_PX = 50;

  useLayoutEffect(() => {
    if (!rowRef.current) return;
    const write = () => {
      const rowH = rowRef.current!.offsetHeight; // 72/88 (+ safe area)
      document.documentElement.style.setProperty("--ys-header-overhang", `${OVERHANG_PX}px`);
      document.documentElement.style.setProperty("--ys-app-header-h", `${rowH}px`);
      document.documentElement.style.setProperty("--ys-app-header-total", `${rowH + OVERHANG_PX}px`);
    };
    write();
    const ro = new ResizeObserver(write);
    ro.observe(rowRef.current!);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const leftConsoleEnabled = isFeatureEnabled("LEFT_CONSOLE");

  return (
    <header className="sticky top-0 z-50 bg-transparent">
      {/* brand bar with gradient shifted right */}
      <div className="relative border-x border-t border-b-0 shadow-[0_10px_18px_-12px_rgba(0,0,0,0.28)] [filter:brightness(1.05)]" style={{ background: 'var(--header-gradient)' }}>
        {/* Top gloss for freshness */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.35),transparent)]" />
        
        {/* icon centered over left rail (240px wide) - fade between states */}
        <Link 
          to="/"
          className="absolute -translate-x-1/2 top-2 z-10 transition-all duration-200 ease-in-out cursor-pointer" 
          style={{ left: isLeftRailCollapsed ? '40px' : '120px' }}
          title="YieldSocial - Home"
        >
          <img
            src={logo}
            alt="YieldSocial"
            className={`h-[72px] w-auto transition-opacity duration-200 ${isLeftRailCollapsed ? 'opacity-0' : 'opacity-100'}`}
          />
          <img
            src={yieldSocialIcon}
            alt="YieldSocial"
            className={`absolute top-0 left-1/2 -translate-x-1/2 h-14 w-auto transition-opacity duration-200 ${isLeftRailCollapsed ? 'opacity-100' : 'opacity-0'}`}
          />
        </Link>
        
        {/* fixed-height row so header chrome never grows */}
        <div
          ref={rowRef}
          className="relative h-[66px] md:h-[72px] flex items-center justify-end gap-2 px-4 mx-auto max-w-2xl w-full"
          style={{ paddingTop: "max(0px, env(safe-area-inset-top))" }}
        >
          {/* right actions (keep compact) */}
          <div className="flex items-center gap-2">
            {leftConsoleEnabled && <MobileLeftConsole />}
          </div>
        </div>
      </div>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuth} 
        onClose={() => setShowAuth(false)} 
      />
    </header>
  );
};
